import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import ProductCard from '../components/ProductCard';

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(searchParams.get('categoria') || 'todos');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const cat = searchParams.get('categoria');
    if (cat) setActiveCategory(cat);
  }, [searchParams]);

  useEffect(() => {
    fetchProducts();
  }, [activeCategory]);

  const fetchCategories = async () => {
    const { data } = await supabase
      .from('categories')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');
    if (data) setCategories(data);
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let query = supabase.from('products').select('*').eq('is_active', true);
      if (activeCategory !== 'todos') {
        query = query.eq('category', activeCategory);
      }
      const { data } = await query.order('is_featured', { ascending: false }).order('name');
      if (data) setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (catId) => {
    setActiveCategory(catId);
    if (catId === 'todos') {
      setSearchParams({});
    } else {
      setSearchParams({ categoria: catId });
    }
  };

  const allCategories = [{ id: 'todos', emoji: '🛒', name: 'Todo' }, ...categories];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen pt-20 md:pt-24 pb-16"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#808E83]">Catálogo</span>
          <h1 className="font-cabinet font-bold text-3xl sm:text-4xl text-[#1A231C] mt-2">Nuestros Productos</h1>
          <p className="text-[#4A5D4E] mt-2 max-w-lg mx-auto">
            Productos frescos y artesanales directamente de Chiloé. Entrega a domicilio o retiro en local.
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {allCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryChange(cat.id)}
              className={`category-pill flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                activeCategory === cat.id
                  ? 'active bg-[#2C5530] text-white border-[#2C5530]'
                  : 'bg-white border-[#E8EAE6] text-[#4A5D4E] hover:border-[#2C5530]'
              }`}
            >
              <span>{cat.emoji}</span>
              <span className="font-medium text-sm">{cat.name}</span>
            </button>
          ))}
        </div>

        {/* Espacio Fresco Banner */}
        {activeCategory === 'espacio-fresco' && (
          <div className="mb-8 p-6 bg-[#2C5530] text-white rounded-2xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h2 className="font-cabinet font-bold text-xl mb-2">🥗 Espacio Fresco - Vegetales Listos</h2>
                <p className="text-white/80 text-sm">
                  Vegetales cortados y empacados al vacío. Frescos, limpios y listos para cocinar.
                  Disponibles para entrega a domicilio o retiro en local.
                </p>
              </div>
              <a
                href="https://wa.me/56994798582?text=Hola!+Me+interesa+el+Espacio+Fresco"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold px-5 py-2 rounded-full transition-colors"
              >
                Consultar por WhatsApp
              </a>
            </div>
          </div>
        )}

        {/* Pan Banner */}
        {activeCategory === 'pan' && (
          <div className="mb-8 p-6 bg-[#E2B35B] text-[#1A231C] rounded-2xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h2 className="font-cabinet font-bold text-xl mb-2">🥖 Pan Fresco de la Mejor Calidad</h2>
                <p className="text-[#1A231C]/80 text-sm">
                  Pan artesanal recién horneado todos los días desde las 8:00 am.
                  Ven a buscarlo a nuestra sala de venta o pídelo a domicilio.
                </p>
              </div>
              <a
                href="https://wa.me/56994798582?text=Hola!+Quiero+reservar+pan+fresco"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 bg-[#1A231C] hover:bg-[#2C5530] text-white font-bold px-5 py-2 rounded-full transition-colors"
              >
                Reservar Pan
              </a>
            </div>
          </div>
        )}

        {/* Gourmet Banner */}
        {activeCategory === 'gourmet' && (
          <div className="mb-8 p-6 bg-gradient-to-r from-[#D96C4A] to-[#E57D5C] text-white rounded-2xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h2 className="font-cabinet font-bold text-xl mb-2">🦀 Empanadas Gourmet de Chiloé</h2>
                <p className="text-white/90 text-sm">
                  Elaboradas artesanalmente con mariscos frescos. Stock limitado - se muestra la cantidad disponible.
                  El stock se descuenta automáticamente al comprar.
                </p>
              </div>
              <a
                href="https://wa.me/56994798582?text=Hola!+Me+interesan+las+empanadas+gourmet"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 bg-white text-[#D96C4A] font-bold px-5 py-2 rounded-full hover:bg-white/90 transition-colors"
              >
                Consultar Stock
              </a>
            </div>
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl h-72 animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-5xl mb-4">🔍</div>
            <h3 className="font-cabinet font-semibold text-xl mb-2">No hay productos disponibles</h3>
            <p className="text-[#808E83]">Prueba con otra categoría</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
